import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const INDUSTRIES = ["Manifatturiero", "Commercio", "Servizi", "Finanza", "Costruzioni", "Energia", "ICT", "Sanità", "Altro"];
const REVENUE_RANGES = ["< 1M", "1M - 10M", "10M - 50M", "50M - 200M", "200M - 1B", "> 1B"];
const EMPLOYEE_RANGES = ["1-9", "10-49", "50-249", "250-999", "1000+"];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", password: "",
    organization_name: "", vat_number: "", industry: "Servizi",
    annual_revenue_range: "1M - 10M", employee_count_range: "10-49", is_pmi: false,
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account creato");
      nav("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore registrazione");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4" data-testid="register-page">
      <div className="bg-white border border-slate-200 rounded-sm p-10 max-w-2xl w-full">
        <div className="font-display font-extrabold text-2xl tracking-tight text-slate-900">TCF<span className="text-slate-400">.</span>Platform</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-6">Crea la tua organizzazione</h1>
        <p className="text-slate-600 text-sm mt-2">Bastano pochi secondi per iniziare a costruire il tuo Tax Control Framework.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="label-tag">Nome e cognome</Label>
              <Input required value={form.full_name} onChange={(e) => upd("full_name", e.target.value)} className="mt-1 rounded-sm" data-testid="reg-name" />
            </div>
            <div>
              <Label className="label-tag">Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} className="mt-1 rounded-sm" data-testid="reg-email" />
            </div>
            <div>
              <Label className="label-tag">Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => upd("password", e.target.value)} className="mt-1 rounded-sm" data-testid="reg-password" />
            </div>
            <div>
              <Label className="label-tag">Ragione sociale</Label>
              <Input required value={form.organization_name} onChange={(e) => upd("organization_name", e.target.value)} className="mt-1 rounded-sm" data-testid="reg-org-name" />
            </div>
            <div>
              <Label className="label-tag">Partita IVA</Label>
              <Input value={form.vat_number} onChange={(e) => upd("vat_number", e.target.value)} className="mt-1 rounded-sm" data-testid="reg-vat" />
            </div>
            <div>
              <Label className="label-tag">Settore</Label>
              <Select value={form.industry} onValueChange={(v) => upd("industry", v)}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="reg-industry"><SelectValue /></SelectTrigger>
                <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Fatturato annuo</Label>
              <Select value={form.annual_revenue_range} onValueChange={(v) => upd("annual_revenue_range", v)}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="reg-revenue"><SelectValue /></SelectTrigger>
                <SelectContent>{REVENUE_RANGES.map((i) => <SelectItem key={i} value={i}>€ {i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Dipendenti</Label>
              <Select value={form.employee_count_range} onValueChange={(v) => upd("employee_count_range", v)}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="reg-employees"><SelectValue /></SelectTrigger>
                <SelectContent>{EMPLOYEE_RANGES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_pmi} onChange={(e) => upd("is_pmi", e.target.checked)} data-testid="reg-pmi" />
            La mia azienda è una PMI (regime semplificato opzionale)
          </label>
          <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm h-11" data-testid="register-submit">
            {loading ? "Creazione…" : "Crea account e inizia"}
          </Button>
        </form>
        <div className="mt-6 text-sm text-slate-500">
          Hai già un account? <Link to="/login" className="text-slate-900 font-medium underline">Accedi</Link>
        </div>
      </div>
    </div>
  );
}
